/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

// Add third-party dependencies.
import { Box, Typography, Link, Divider } from '@mui/material';

/**
 * Terms of Service content component.
 * @example
 * return <TermsContent />
 * @returns {React.ReactElement} The Terms of Service content component.
 */
const TermsContent: React.FC = () => (
  <Box>
    <Typography variant="body1" paragraph>
      These Terms of Service (&quot;Terms&quot;) govern your use of the
      tools.dance platform and services provided by Little Shilling, Inc., an S
      Corporation organized under the laws of California (&quot;Company&quot;,
      &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). By accessing or using
      our services, you agree to be bound by these Terms.
    </Typography>

    <Divider sx={{ my: 3 }} />

    {/* Section 1: Service Description */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      1. Service Description
    </Typography>
    <Typography variant="body1" paragraph>
      tools.dance is a comprehensive platform designed for dance studio owners
      to manage their operations and enhance their business capabilities. Our
      services include:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Widget Services:</strong> Student birthday widgets, holiday
        message widgets, teacher bio widgets, announcements and milestone
        widgets
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Management Tools:</strong> Schedule creator and editor, contact
        list manager, costume planner, student leveling tools
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Integration Services:</strong> Sales receipt uploader for
        QuickBooks integration, Studio Pro data formatting and uploads
      </Typography>
      <Typography component="li" variant="body1">
        <strong>AI-Powered Features:</strong> AI chat agents for client
        questions, growth AI tools for financial analysis and recommendations
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Content Tools:</strong> Newsletter formatter and styling tools
      </Typography>
    </Box>

    {/* Section 2: Account Terms */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      2. Account Terms and Eligibility
    </Typography>
    <Typography variant="body1" paragraph>
      You must be at least 13 years old to use our services. By creating an
      account, you represent that you are of legal age to form a binding
      contract and are not barred from receiving services under applicable law.
    </Typography>
    <Typography variant="body1" paragraph>
      You are responsible for maintaining the security of your account and
      password. You agree to accept responsibility for all activities that occur
      under your account.
    </Typography>

    {/* Section 3: Subscription and Billing */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      3. Subscription and Billing
    </Typography>
    <Typography variant="body1" paragraph>
      We offer both free and paid subscription tiers:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Free Tier:</strong> Access to basic tools with watermarks on
        generated content and widgets
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Paid Tiers:</strong> Access to advanced features, removal of
        watermarks, and premium tools
      </Typography>
    </Box>
    <Typography variant="body1" paragraph>
      We reserve the right to change our pricing at any time. Price changes will
      be communicated to existing subscribers with at least 30 days notice
      before the next billing cycle.
    </Typography>

    {/* Section 4: Data and Privacy */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      4. Data Usage and Privacy
    </Typography>
    <Typography variant="body1" paragraph>
      You retain ownership of all data you upload to our platform, including
      student information, financial data, and studio content. We use your data
      solely to provide our services and will not share it with third parties
      except as necessary to deliver our services (such as QuickBooks
      integration) or as required by law.
    </Typography>
    <Typography variant="body1" paragraph>
      We retain your data while your account is active and for 30 days after
      account cancellation to allow for account recovery. You may request
      immediate deletion of your data by contacting us.
    </Typography>

    {/* Section 5: Widget and Embedding Terms */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      5. Widget and Website Integration
    </Typography>
    <Typography variant="body1" paragraph>
      Our widget services allow you to embed tools.dance content on your studio
      website. You grant us permission to display your content through these
      widgets. You are responsible for ensuring you have the right to display
      any content (including student photos and information) through our
      widgets.
    </Typography>
    <Typography variant="body1" paragraph>
      Free tier widgets will include tools.dance watermarks. Paid subscribers
      may remove watermarks from their embedded content.
    </Typography>

    {/* Section 6: AI Services and Disclaimers */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      6. AI Services and Disclaimers
    </Typography>
    <Typography variant="body1" paragraph>
      Our AI-powered features, including chat agents and growth analysis tools,
      are provided for informational purposes only. While we strive for
      accuracy, AI-generated content and recommendations should not be
      considered as professional financial, legal, or business advice.
    </Typography>
    <Typography variant="body1" paragraph>
      You are solely responsible for any decisions made based on AI-generated
      recommendations. We recommend consulting with qualified professionals for
      important business decisions.
    </Typography>

    {/* Section 7: Financial Tools Disclaimer */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      7. Financial Tools and Integration
    </Typography>
    <Typography variant="body1" paragraph>
      Our financial analysis tools and QuickBooks integration features are
      provided for convenience and should not be considered as professional
      accounting or financial advice. You are responsible for the accuracy of
      all financial data and for compliance with applicable accounting standards
      and tax requirements.
    </Typography>
    <Typography variant="body1" paragraph>
      We are not responsible for any errors in financial calculations, data
      transfers, or integration issues with third-party services.
    </Typography>

    {/* Section 8: Acceptable Use */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      8. Acceptable Use Policy
    </Typography>
    <Typography variant="body1" paragraph>
      You agree not to use our services for any unlawful purpose or in any way
      that could damage, disable, or impair our services. You may not attempt to
      gain unauthorized access to our systems or interfere with other
      users&apos; access to our services.
    </Typography>
    <Typography variant="body1" paragraph>
      You are responsible for ensuring that any student data you upload complies
      with applicable privacy laws and that you have appropriate consent for
      data processing.
    </Typography>

    {/* Section 9: Intellectual Property */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      9. Intellectual Property
    </Typography>
    <Typography variant="body1" paragraph>
      The tools.dance platform, including all software, designs, and content
      created by us, is owned by Little Shilling, Inc. and protected by
      intellectual property laws. You may not copy, modify, or distribute our
      proprietary content without permission.
    </Typography>
    <Typography variant="body1" paragraph>
      You retain ownership of all content you create or upload to our platform.
      By using our services, you grant us a limited license to use your content
      solely for the purpose of providing our services to you.
    </Typography>

    {/* Section 10: Limitation of Liability */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      10. Limitation of Liability
    </Typography>
    <Typography variant="body1" paragraph>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY TO YOU FOR ANY
      CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR OUR SERVICES SHALL NOT
      EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE
      CLAIM.
    </Typography>
    <Typography variant="body1" paragraph>
      WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
      CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF
      PROFITS, DATA, OR BUSINESS OPPORTUNITIES.
    </Typography>

    {/* Section 11: Termination */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      11. Termination
    </Typography>
    <Typography variant="body1" paragraph>
      You may terminate your account at any time by contacting us. We may
      terminate or suspend your account if you violate these Terms or for any
      other reason with reasonable notice.
    </Typography>
    <Typography variant="body1" paragraph>
      Upon termination, your access to our services will cease, and we will
      delete your data according to our data retention policy unless legally
      required to retain it.
    </Typography>

    {/* Section 12: Governing Law */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      12. Governing Law and Disputes
    </Typography>
    <Typography variant="body1" paragraph>
      These Terms are governed by the laws of the State of California, without
      regard to conflict of law principles. Any disputes arising from these
      Terms or our services shall be resolved in the state or federal courts
      located in California.
    </Typography>

    {/* Section 13: Changes to Terms */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      13. Changes to These Terms
    </Typography>
    <Typography variant="body1" paragraph>
      We may update these Terms from time to time. We will notify you of any
      material changes by posting the new Terms on our platform and updating the
      &quot;Last updated&quot; date. Your continued use of our services after
      such changes constitutes acceptance of the new Terms.
    </Typography>

    {/* Section 14: Contact Information */}
    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      14. Contact Information
    </Typography>
    <Typography variant="body1" paragraph>
      If you have any questions about these Terms of Service, please contact us
      through our{' '}
      <Link href="/contact" target="_blank" rel="noopener noreferrer">
        contact page
      </Link>
      .
    </Typography>

    <Divider sx={{ my: 4 }} />

    <Typography variant="body2" color="text.secondary" align="center">
      © 2025 Little Shilling, Inc. All rights reserved.
    </Typography>
  </Box>
);

export default TermsContent;
